import { FormControl, FormControlProps } from 'baseui/form-control';
import { themedStyled } from 'common/styles/styletron.theme';
import * as React from 'react';

import {
  CustomFormControlWrapper,
  LabelContainer,
  RequiredText,
  RightEnhancerContainer,
} from './styles';
import Text from './text';

export enum TextLabelPlacementEnum {
  Default = 'default',
  Left = 'left',
  TopLeft = 'top-left',
}

export interface CustomFormControlProps extends FormControlProps {
  hideLabel?: boolean;
  required?: boolean;
  noMargin?: boolean;
  leftPercentage?: number;
  labelRightEnhancer?: React.ReactNode;
  labelPlacement?: TextLabelPlacementEnum;
  hideRequiredStar?: boolean;
}

export function CustomLabel(props: any) {
  const {
    label,
    labelPlacement,
    hideLabel,
    disabled,
    hideRequiredStar,
    required,
    $labelRightEnhancer,
    $style,
    ...restProps
  } = props;
  const opacity = React.useMemo(() => {
    if (disabled) {
      return 0.5;
    }
    if (hideLabel) {
      return 0;
    }
    return 1;
  }, [disabled, hideLabel]);
  return (
    <LabelContainer $style={$style}>
      {!hideLabel && (
        <Text
          style={{
            display: 'block',
            whiteSpace: 'nowrap',
            opacity,
          }}
          variant="body2Regular"
          {...restProps}
        >
          {label}
        </Text>
      )}
      {!disabled && required && !hideRequiredStar && (
        <RequiredText>*</RequiredText>
      )}
      {$labelRightEnhancer && (
        <RightEnhancerContainer>{$labelRightEnhancer}</RightEnhancerContainer>
      )}
    </LabelContainer>
  );
}

export default function CustomFormControl(props: CustomFormControlProps) {
  const {
    hideLabel = false,
    noMargin = false,
    required = false,
    children,
    label,
    leftPercentage,
    labelRightEnhancer,
    labelPlacement = TextLabelPlacementEnum.Default,
    ...restProps
  } = props;

  const ref = React.useRef<HTMLDivElement | null>();

  React.useEffect(() => {
    if (props.error) {
      ref.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [props.error]);
  return (
    <CustomFormControlWrapper ref={(el) => (ref.current = el)}>
      <FormControl
        {...restProps}
        overrides={{
          ControlContainer: {
            style: {
              marginBottom: noMargin ? '0px' : '16px',
            },
          },
          // Label: {
          //   style: {
          //     color,
          //     opacity: hideLabel ? 0 : 1,
          //   },
          //   component: (props: any) => {
          //     return (
          //       <CustomLabel
          //         required={required}
          //         {...props}
          //         $labelRightEnhancer={labelRightEnhancer}
          //       />
          //     );
          //   },
          // },
        }}
      >
        <FormContainer labelPlacement={labelPlacement}>
          <CustomLabelContainer
            leftPercentage={leftPercentage}
            labelPlacement={labelPlacement}
          >
            <CustomLabel
              required={required}
              {...restProps}
              label={label}
              $labelRightEnhancer={labelRightEnhancer}
              hideLabel={hideLabel}
            />
          </CustomLabelContainer>
          <ContentContainer
            leftPercentage={leftPercentage}
            labelPlacement={labelPlacement}
          >
            {children}
          </ContentContainer>
        </FormContainer>
      </FormControl>
    </CustomFormControlWrapper>
  );
}

const CustomLabelContainer = themedStyled<
  'div',
  { labelPlacement?: TextLabelPlacementEnum; leftPercentage?: number }
>('div', ({ labelPlacement, leftPercentage }) => ({
  ...(labelPlacement === TextLabelPlacementEnum.Default
    ? {
        marginBottom: '4px',
      }
    : {
        width: leftPercentage ? `${leftPercentage}%` : '30%',
        marginInline: '4px',
      }),
}));

const ContentContainer = themedStyled<
  'div',
  { labelPlacement?: TextLabelPlacementEnum; leftPercentage?: number }
>('div', ({ labelPlacement, leftPercentage }) => ({
  ...(labelPlacement === TextLabelPlacementEnum.Default
    ? {}
    : {
        width: leftPercentage ? `calc(100% - ${leftPercentage}%)` : '70%',
        marginLeft: '16px',
      }),
}));

const FormContainer = themedStyled<
  'div',
  { labelPlacement?: TextLabelPlacementEnum }
>('div', ({ labelPlacement }) => ({
  ...(labelPlacement === TextLabelPlacementEnum.Default
    ? {}
    : labelPlacement === TextLabelPlacementEnum.TopLeft
    ? {
        display: 'flex',
        alignItems: 'flex-start',
      }
    : {
        display: 'flex',
        alignItems: 'center',
      }),
}));
